import React, { useState, useEffect } from "react";
import { observer } from "mobx-react-lite";
import { classNames } from "../helpers";
import { useTranslation } from "react-i18next";

interface Tab {
  name: string;
  icon?: React.ComponentType<{ className?: string }>;
}

interface TabViewProps {
  tabs: Tab[];
  onChange?: (tabName: string) => void;
  activeTab?: string;
  defaultTab?: string;
}

export const TabView = observer((props: TabViewProps) => {
  const { tabs, onChange, activeTab: controlledActiveTab, defaultTab } = props;
  const [internalActiveTab, setInternalActiveTab] = useState(
    defaultTab || tabs[0]?.name || ""
  );
  const { t } = useTranslation("components");

  // Use controlled tab if provided, otherwise use internal state
  const activeTab = controlledActiveTab ?? internalActiveTab;

  useEffect(() => {
    if (defaultTab && !controlledActiveTab) {
      setInternalActiveTab(defaultTab);
    }
  }, [defaultTab, controlledActiveTab]);

  const handleTabChange = (tabName: string) => {
    if (!controlledActiveTab) {
      setInternalActiveTab(tabName);
    }
    onChange?.(tabName);
  };

  return (
    <>
      <div className="sm:hidden mb-4">
        <label htmlFor="tabs" className="sr-only">
          {t("select_tab")}
        </label>
        <select
          id="tabs"
          name="tabs"
          className="p-3 block w-full focus:ring-indigo-500 focus:border-indigo-500 border-gray-300 rounded-lg border bg-white"
          value={activeTab}
          onChange={(e) => handleTabChange(e.target.value)}
        >
          {tabs.map((tab) => (
            <option key={tab.name} value={tab.name}>
              {tab.name}
            </option>
          ))}
        </select>
      </div>
      <div className="hidden sm:block">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-6" aria-label="Tabs">
            {tabs.map((tab) => {
              const isActiveTab = activeTab === tab.name;
              return (
                <button
                  key={tab.name}
                  type="button"
                  className={classNames(
                    isActiveTab
                      ? "border-indigo-500 text-indigo-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300",
                    "group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200"
                  )}
                  aria-current={isActiveTab ? "page" : undefined}
                  onClick={() => handleTabChange(tab.name)}
                >
                  {tab.icon && (
                    <tab.icon
                      className={classNames(
                        isActiveTab
                          ? "text-indigo-500"
                          : "text-gray-400 group-hover:text-gray-500",
                        "mr-2 h-5 w-5"
                      )}
                      aria-hidden="true"
                    />
                  )}
                  <span>{tab.name}</span>
                </button>
              );
            })}
          </nav>
        </div>
      </div>
    </>
  );
});
